import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NewPageTitleBar from '@/components/shared/NewPageTitleBar';
import FilterShopAllDateRange from '@/components/filters/FilterShopAllDateRange';
import AppCard from '@/components/appcard/AppCard';
import AppSectionLoader from '@/components/AppSectionLoader';
import WcnApexchart from '@/components/charts/WcnApexchart';
import { getMenuData } from '@/core/data/menuData';
import { apiClient } from '@core/api';
import { useNotifications } from '@/core/hooks/useNotifications';
import { useApiStore } from '@core/store';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { DateTime } from 'luxon';
import { FilterRef } from '@features/statistics/types.ts';

interface GateOpeningData {
    shopName: string;

    [key: string]: string | number;
}

interface SeriesData {
    name: string;
    data: Array<{ x: string; y: number }>;
}

interface FilterArgs {
    shopIds: string[];
    start: string;
    end: string;
}

const GatesOpenings: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifyErrorResponse } = useNotifications();
    const { shopIds } = useApiStore();
    const [dataLoading, setDataLoading] = useState(false);
    const [series, setSeries] = useState<SeriesData[]>([]);
    const [maxValue, setMaxValue] = useState(5);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const filterRef = useRef<FilterRef>(null);

    const loadData = async (args: FilterArgs) => {
        setDataLoading(true);
        const url = `statistics/wanzlGates/openings/${args.start}/${args.end}/?shopId=${args.shopIds.join('&shopId=')}`;

        try {
            const response = await apiClient.get<GateOpeningData[]>(url);
            let data = response.data;

            setLastUpdate(DateTime.now());

            if (!Array.isArray(data)) {
                console.error('Expected array but got:', data);
                setSeries([]);
                setMaxValue(5);
                return;
            }

            if (args.shopIds && args.shopIds.length > 0) {
                const selectedShopNames = shopIds
                    .filter((shop) => args.shopIds.includes(shop.id))
                    .map((shop) => shop.name);

                data = data.filter((item) => selectedShopNames.includes(item.shopName));
            }

            const newSeries: SeriesData[] = [];
            let max = 0;

            data.forEach((element) => {
                let elMax = 0;
                Object.entries(element)
                    .filter(([, value]) => typeof value === 'number')
                    .forEach(([key, value]) => {
                        elMax += value as number;
                        const seriesName = t(key);
                        const seriesIndex = newSeries.findIndex((x) => x.name === seriesName);
                        if (seriesIndex > -1) {
                            newSeries[seriesIndex].data.push({
                                x: element.shopName,
                                y: value as number
                            });
                        } else {
                            const count = newSeries.push({ name: seriesName, data: [] });
                            newSeries[count - 1].data.push({
                                x: element.shopName,
                                y: value as number
                            });
                        }
                    });
                max = elMax > max ? elMax : max;
            });

            setMaxValue(max > 0 ? max : 5);
            setSeries(newSeries);
        } catch (error) {
            notifyErrorResponse(error);
            setSeries([]);
            setMaxValue(5);
        } finally {
            setDataLoading(false);
        }
    };

    const handleReload = useCallback(async () => {
        filterRef.current?.refresh();
    }, []);

    useRegisterRefresh(handleReload);

    return (
        <div>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-view-carousel" />}
                title={`${t('gates')} ${t('openings')}`}
                iconSize="20px"
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/gates/openings' }}
                lastUpdate={lastUpdate}
                t={t}
            />
            <div className="container grid-list-xl no-padding-top container--fluid">
                <FilterShopAllDateRange ref={filterRef} allShopsSelectedOnStart={false} onLoadData={loadData} />
                <div className="row">
                    <AppSectionLoader status={dataLoading} />
                    <AppCard
                        heading={`${t('gates')} ${t('openings')}`}
                        icon="view_carousel"
                        colClasses="col-spacing"
                        customClasses="no-padding-bottom"
                        fullBlock={false}
                        footer={false}
                    >
                        <WcnApexchart
                            type="bar"
                            series={series}
                            height={550}
                            chartId="gatesOpeningsChart"
                            stacked={true}
                            dataLabelsEnabled={true}
                            xAxisType="category"
                            yAxisMin={0}
                            yAxisMax={maxValue}
                            tooltipShared={true}
                        />
                    </AppCard>
                </div>
            </div>
        </div>
    );
};

export default GatesOpenings;
